import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import './gilFonts.css';

/** Colours of the GIL report designs (Figma "GIL", 1920 wide). */
export const GIL = {
  font: "'GIL Poppins', 'Poppins', sans-serif",
  /** The tables are set in Inter. */
  tableFont: "'Inter', 'GIL Poppins', 'Poppins', sans-serif",
  text: '#1E293B',
  muted: '#64748B',
  soft: '#94A3B8',
  label: '#3B5270',
  border: '#E2E8F0',
  surface: '#F8FAFC',
  zebra: '#FCFDFE',
  green: '#0AA34F',
  track: '#E2E8F0',
  completion: '#0091D0',
  /** Section titles and big figures. */
  heading: '#000000',
  /** Strapline under a section title. */
  strap: '#8790B0',
  /** Soft caption under a stat tile's label. */
  caption: '#8A9BB3',
  /** Active tab. */
  tab: '#11A465',
  tableHead: '#6B7280',
  tableHeadBg: '#F6F8F9',
  tableBorder: '#E5E7EB',
  cell: '#374151',
  cellName: '#1F2937',
  cellSub: '#9CA3AF',
};

/**
 * A Figma pixel size, scaled with the viewport from lg up like the rest of the
 * app. Large display sizes shrink on phones; small text stays as designed.
 * Tablets get a fixed size, since a 1920-wide vw value is half size on an iPad.
 */
export const gilSize = (desktop: number, mobile?: number) => ({
  xs: pxToRem(
    mobile ??
      (desktop <= 14 ? desktop : Math.max(14, Math.round(desktop * 0.62)))
  ),
  md: pxToRem(desktop <= 16 ? desktop : Math.round(16 + (desktop - 16) * 0.6)),
  lg: pxTovW(desktop),
});

/** Font size, weight and colour of one Figma text style, in Poppins. */
export const gilText = (
  size: number,
  weight: 400 | 500 | 600 | 700,
  color: string = GIL.text
) => ({
  fontFamily: GIL.font,
  fontSize: gilSize(size),
  fontWeight: weight,
  lineHeight: 1.5,
  color,
});

/** One Figma text style of the report tables, in Inter. */
export const gilTableText = (
  size: number,
  weight: 400 | 500 | 600 | 700,
  color: string = GIL.cell
) => ({
  fontFamily: GIL.tableFont,
  fontSize: gilSize(size),
  fontWeight: weight,
  lineHeight: 1.2,
  color,
});

/** Cell text styles shared by every report table (Figma "Table"). */
export const GIL_CELL = {
  /** The "#" column. */
  index: gilTableText(16, 700),
  /** An underlined name that drills down to the next report. */
  link: {
    ...gilTableText(16, 500, GIL.cellName),
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': { color: GIL.green },
  },
  /** Grey line under a name, like "11 Students". */
  sub: gilTableText(12, 400, GIL.cellSub),
  text: gilTableText(14, 400),
  /** Scores and other figures that read a little heavier. */
  strong: gilTableText(14, 500),
};
